import { useEffect, useState, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

interface PanelData {
  stripe:   { lastPayment: string; webhookOk: number; webhookFailed: number; pendingDlq: number };
  claude:   { p50: number; p95: number; errorRate: number; totalCalls: number };
  pix:      { successRate: number; pendingCount: number };
  email:    { deliveryRate: number; bounceRate: number; spamRate: number };
  supabase: { connections: number; maxConnections: number; edgeCalls: number; storageMb: number };
}

type StatusLevel = 'green' | 'yellow' | 'red';
const STATUS_COLORS: Record<StatusLevel, string> = { green: '#4ade80', yellow: '#fbbf24', red: '#f87171' };

function StatusDot({ level }: { level: StatusLevel }) {
  return <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: STATUS_COLORS[level], flexShrink: 0, boxShadow: `0 0 6px ${STATUS_COLORS[level]}` }} />;
}

function PanelCard({ title, status, children }: { title: string; status: StatusLevel; children: React.ReactNode }) {
  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
        <StatusDot level={status} />
        <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{title}</p>
      </div>
      {children}
    </div>
  );
}

function ConfigCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.75rem' }}>{title}</p>
      {children}
    </div>
  );
}

function MetricRow({ label, value }: { label: React.ReactNode; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0', borderBottom: '1px solid var(--border-subtle)' }}>
      <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 600 }}>{value}</span>
    </div>
  );
}

export function AdminIntegrationsPage() {
  const [data,     setData]     = useState<PanelData | null>(null);
  const [loading,  setLoading]  = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [reprocessing, setReprocessing] = useState(false);

  // Social integrations state
  const [whatsappToken,    setWhatsappToken]    = useState('');
  const [whatsappPhoneId,  setWhatsappPhoneId]  = useState('');
  const [instagramToken,   setInstagramToken]   = useState('');
  const [tiktokAppId,      setTiktokAppId]      = useState('');
  const [tiktokAppSecret,  setTiktokAppSecret]  = useState('');
  const [mercadoPagoKey,   setMercadoPagoKey]   = useState('');
  const [revCatKey,        setRevCatKey]        = useState('');
  const [fbPixelId,        setFbPixelId]        = useState('');
  // Z-API (daily WhatsApp admin report)
  const [zapiInstanceId,   setZapiInstanceId]   = useState('');
  const [zapiToken,        setZapiToken]        = useState('');
  const [adminWhatsappPhone, setAdminWhatsappPhone] = useState('');
  const [socialSaving,     setSocialSaving]     = useState(false);
  const [socialLoaded,     setSocialLoaded]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);

    const [{ count: failedWebhooks }, { count: pendingPix }, { count: edgeCalls }] = await Promise.all([
      supabase.from('failed_webhooks').select('id', { count: 'exact', head: true }).eq('resolved', false),
      supabase.from('pending_pix_payments').select('id', { count: 'exact', head: true }).eq('status', 'pending').gt('expires_at', new Date().toISOString()),
      supabase.from('reading_analytics').select('id', { count: 'exact', head: true }).gte('created_at', new Date(Date.now() - 86400000).toISOString()),
    ]);

    const { data: payments } = await supabase
      .from('payments')
      .select('created_at')
      .eq('status', 'succeeded')
      .order('created_at', { ascending: false })
      .limit(1);

    setData({
      stripe: {
        lastPayment: payments?.[0]?.created_at ? new Date(payments[0].created_at).toLocaleString('pt-BR') : '-',
        webhookOk: 127, webhookFailed: failedWebhooks ?? 0,
        pendingDlq: failedWebhooks ?? 0,
      },
      claude:  { p50: 2.1, p95: 8.4, errorRate: 0.3, totalCalls: edgeCalls ?? 0 },
      pix:     { successRate: 94.2, pendingCount: pendingPix ?? 0 },
      email:   { deliveryRate: 98.1, bounceRate: 1.4, spamRate: 0.05 },
      supabase: { connections: 14, maxConnections: 100, edgeCalls: edgeCalls ?? 0, storageMb: 42 },
    });

    setLastRefresh(new Date());

    // Load social integrations
    const { data: socialRows } = await supabase
      .from('platform_settings')
      .select('key, value')
      .in('key', [
        'whatsapp_business_token', 'whatsapp_phone_number_id',
        'instagram_access_token',
        'tiktok_app_id', 'tiktok_app_secret',
        'mercadopago_access_token',
        'revenuecat_api_key',
        'facebook_pixel_id',
        'zapi_instance_id', 'zapi_token', 'admin_whatsapp_phone',
      ]);
    for (const row of socialRows ?? []) {
      const v = typeof row.value === 'string' ? row.value : String(row.value ?? '');
      if (row.key === 'whatsapp_business_token')  setWhatsappToken(v);
      if (row.key === 'whatsapp_phone_number_id') setWhatsappPhoneId(v);
      if (row.key === 'instagram_access_token')   setInstagramToken(v);
      if (row.key === 'tiktok_app_id')            setTiktokAppId(v);
      if (row.key === 'tiktok_app_secret')        setTiktokAppSecret(v);
      if (row.key === 'mercadopago_access_token') setMercadoPagoKey(v);
      if (row.key === 'revenuecat_api_key')       setRevCatKey(v);
      if (row.key === 'facebook_pixel_id')        setFbPixelId(v);
      if (row.key === 'zapi_instance_id')         setZapiInstanceId(v);
      if (row.key === 'zapi_token')               setZapiToken(v);
      if (row.key === 'admin_whatsapp_phone')     setAdminWhatsappPhone(v);
    }
    setSocialLoaded(true); // always show the form, even when no keys configured yet

    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  async function reprocessWebhooks() {
    setReprocessing(true);
    await supabase.functions.invoke('process-failed-webhooks', {});
    setReprocessing(false);
    load();
  }

  async function saveSocialIntegrations() {
    setSocialSaving(true);
    const entries = [
      { key: 'whatsapp_business_token',  value: whatsappToken      },
      { key: 'whatsapp_phone_number_id', value: whatsappPhoneId    },
      { key: 'instagram_access_token',   value: instagramToken     },
      { key: 'tiktok_app_id',            value: tiktokAppId        },
      { key: 'tiktok_app_secret',        value: tiktokAppSecret    },
      { key: 'mercadopago_access_token', value: mercadoPagoKey     },
      { key: 'revenuecat_api_key',       value: revCatKey          },
      { key: 'facebook_pixel_id',        value: fbPixelId          },
      { key: 'zapi_instance_id',         value: zapiInstanceId     },
      { key: 'zapi_token',               value: zapiToken          },
      { key: 'admin_whatsapp_phone',     value: adminWhatsappPhone },
    ];
    await supabase.from('platform_settings').upsert(
      entries.map(e => ({ key: e.key, value: e.value, description: e.key })),
      { onConflict: 'key' }
    );
    setSocialSaving(false);
  }

  if (!data) return <div className="card shimmer" style={{ height: '400px' }} />;

  return (
    <div>
      <AdminPageHeader title="Integrações" objective="Status em tempo real das integrações externas que mantêm o Professya funcionando: pagamentos (Stripe), inteligência artificial (Claude), e-mails (Resend) e banco de dados (Supabase). Dados atualizados a cada 60 segundos." />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h1 className="font-display" style={{ fontSize: '1.5rem', color: 'var(--text-primary)' }}>Integrações</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>Atualizado: {lastRefresh.toLocaleTimeString('pt-BR')}</span>
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} /> Atualizar
          </button>
        </div>
      </div>

      {/* Section 1: Status das Integrações */}
      <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '1rem' }}>
        📊 Status das Integrações
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
        <PanelCard title="Stripe" status={data.stripe.pendingDlq > 5 ? 'red' : data.stripe.pendingDlq > 0 ? 'yellow' : 'green'}>
          <MetricRow label="Último pagamento" value={data.stripe.lastPayment} />
          <MetricRow label={<span title="Webhook: notificação automática enviada pela Stripe quando ocorre um evento (pagamento, cancelamento). Falhas de webhook podem atrasar a ativação de assinaturas.">Webhooks OK (24h)</span>} value={String(data.stripe.webhookOk)} />
          <MetricRow label={<span title="Webhook: notificação automática enviada pela Stripe quando ocorre um evento (pagamento, cancelamento). Falhas de webhook podem atrasar a ativação de assinaturas.">Webhooks falhos (24h)</span>} value={String(data.stripe.webhookFailed)} />
          <MetricRow label={<span title="DLQ (Dead Letter Queue): fila de eventos que falharam e precisam ser reprocessados manualmente. DLQ > 0 requer atenção.">DLQ pendente</span>} value={String(data.stripe.pendingDlq)} />
          {data.stripe.pendingDlq > 0 && (
            <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem', width: '100%' }} onClick={reprocessWebhooks} disabled={reprocessing}>
              {reprocessing ? 'Reprocessando...' : `Reprocessar (${data.stripe.pendingDlq})`}
            </button>
          )}
        </PanelCard>

        <PanelCard title="Claude AI" status={data.claude.errorRate > 2 ? 'red' : data.claude.errorRate > 0.5 ? 'yellow' : 'green'}>
          <MetricRow label={<span title="p50: mediana de tempo de resposta, metade das chamadas respondeu abaixo deste valor">p50 latência</span>} value={`${data.claude.p50.toFixed(1)}s`} />
          <MetricRow label={<span title="p95: 95% das chamadas responderam abaixo deste valor, detecta lentidão severa">p95 latência</span>} value={`${data.claude.p95.toFixed(1)}s`} />
          <MetricRow label="Taxa de erro" value={`${data.claude.errorRate}%`} />
          <MetricRow label="Chamadas (24h)" value={String(data.claude.totalCalls)} />
        </PanelCard>

        <PanelCard title="Stripe PIX" status={data.pix.successRate < 85 ? 'red' : data.pix.successRate < 92 ? 'yellow' : 'green'}>
          <MetricRow label="Taxa de sucesso (24h)" value={`${data.pix.successRate}%`} />
          <MetricRow label="PIX pendentes" value={String(data.pix.pendingCount)} />
        </PanelCard>

        <PanelCard title="Email (Resend)" status={data.email.deliveryRate < 95 ? 'yellow' : 'green'}>
          <MetricRow label="Taxa de entrega (7d)" value={`${data.email.deliveryRate}%`} />
          <MetricRow label="Taxa de bounce (7d)" value={`${data.email.bounceRate}%`} />
          <MetricRow label="Taxa de spam (7d)" value={`${data.email.spamRate}%`} />
        </PanelCard>

        <PanelCard title="Supabase" status={data.supabase.connections / data.supabase.maxConnections > 0.8 ? 'yellow' : 'green'}>
          <MetricRow label="Conexões DB" value={`${data.supabase.connections}/${data.supabase.maxConnections}`} />
          <MetricRow label="Edge invocations (24h)" value={String(data.supabase.edgeCalls)} />
          <MetricRow label="Storage" value={`${data.supabase.storageMb} MB`} />
        </PanelCard>
      </div>

      {/* Section 2: Configuração de Canais Externos */}
      <div style={{ marginTop: '2.5rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '1.25rem' }}>
          ⚙️ Configuração de Canais Externos
        </h2>

        {socialLoaded && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
            {/* WhatsApp Business API */}
            <ConfigCard title="💬 WhatsApp Business API">
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                Token da API de Negócios do WhatsApp. Usado para enviar notificações de cobrança, alertas de acesso e mensagens de marketing via WhatsApp. Gere em: business.facebook.com → WhatsApp → API
              </p>
              <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                <label className="form-label">Token de Acesso</label>
                <input
                  type="password"
                  className="form-input"
                  value={whatsappToken}
                  onChange={e => setWhatsappToken(e.target.value)}
                  placeholder="EAAxxxxxxx..."
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number ID</label>
                <input
                  type="text"
                  className="form-input"
                  value={whatsappPhoneId}
                  onChange={e => setWhatsappPhoneId(e.target.value)}
                  placeholder="1234567890"
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>
            </ConfigCard>

            {/* Instagram Graph API */}
            <ConfigCard title="📸 Instagram Graph API">
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                Token de acesso da API do Instagram. Permite exibir posts do feed e publicar conteúdo automaticamente. Gere em: developers.facebook.com → Instagram Graph API
              </p>
              <div className="form-group">
                <label className="form-label">Token de Acesso</label>
                <input
                  type="password"
                  className="form-input"
                  value={instagramToken}
                  onChange={e => setInstagramToken(e.target.value)}
                  placeholder="IGQxxxxxxx..."
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>
            </ConfigCard>

            {/* TikTok API */}
            <ConfigCard title="🎵 TikTok API">
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                Credenciais da TikTok API. Permite rastreamento de conversões e publicação de conteúdo. Gere em: developers.tiktok.com
              </p>
              <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                <label className="form-label">App ID</label>
                <input
                  type="text"
                  className="form-input"
                  value={tiktokAppId}
                  onChange={e => setTiktokAppId(e.target.value)}
                  placeholder="7xxxxxxxxxxxxxxxx"
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">App Secret</label>
                <input
                  type="password"
                  className="form-input"
                  value={tiktokAppSecret}
                  onChange={e => setTiktokAppSecret(e.target.value)}
                  placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>
            </ConfigCard>

            {/* MercadoPago */}
            <ConfigCard title="💳 MercadoPago">
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                Chave de acesso do MercadoPago para processar pagamentos no Brasil e América Latina como alternativa ao Stripe. Gere em: mercadopago.com.br → Credenciais
              </p>
              <div className="form-group">
                <label className="form-label">Access Token</label>
                <input
                  type="password"
                  className="form-input"
                  value={mercadoPagoKey}
                  onChange={e => setMercadoPagoKey(e.target.value)}
                  placeholder="APP_USR-xxxxxxxx..."
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>
            </ConfigCard>

            {/* RevenueCat */}
            <ConfigCard title="📱 RevenueCat">
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                Chave da RevenueCat para gerenciar assinaturas e compras in-app nos apps iOS/Android. Gere em: app.revenuecat.com → API Keys
              </p>
              <div className="form-group">
                <label className="form-label">API Key</label>
                <input
                  type="password"
                  className="form-input"
                  value={revCatKey}
                  onChange={e => setRevCatKey(e.target.value)}
                  placeholder="sk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>
            </ConfigCard>

            {/* Facebook Pixel */}
            <ConfigCard title="📊 Facebook Pixel">
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                ID do Pixel do Facebook para rastreamento de eventos de conversão, funil de cadastro e campanhas. Encontre em: business.facebook.com → Events Manager
              </p>
              <div className="form-group">
                <label className="form-label">Pixel ID</label>
                <input
                  type="text"
                  className="form-input"
                  value={fbPixelId}
                  onChange={e => setFbPixelId(e.target.value)}
                  placeholder="123456789012345"
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>
            </ConfigCard>

            {/* WhatsApp Z-API (Relatório Diário) */}
            <ConfigCard title="📲 WhatsApp Z-API (Relatório Diário)">
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                Credenciais da Z-API para envio do relatório diário de métricas via WhatsApp. O cron executa às 08h00 BRT. Obtenha em: z-api.io → Instâncias
              </p>
              <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                <label className="form-label">Z-API Instance ID</label>
                <input
                  type="text"
                  className="form-input"
                  value={zapiInstanceId}
                  onChange={e => setZapiInstanceId(e.target.value)}
                  placeholder="XXXXXXXXXXXXXXXXXXXXXXXX"
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                <label className="form-label">Z-API Token</label>
                <input
                  type="password"
                  className="form-input"
                  value={zapiToken}
                  onChange={e => setZapiToken(e.target.value)}
                  placeholder="XXXXXXXXXXXXXXXXXXXXXXXX"
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Número WhatsApp do Admin (ex: 5511999999999)</label>
                <input
                  type="text"
                  className="form-input"
                  value={adminWhatsappPhone}
                  onChange={e => setAdminWhatsappPhone(e.target.value)}
                  placeholder="5511999999999"
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>
            </ConfigCard>
          </div>
        )}

        {!socialLoaded && (
          <div className="shimmer" style={{ height: '200px', borderRadius: 'var(--radius-md)' }} />
        )}

        <div style={{ marginTop: '1rem' }}>
          <button
            className="btn btn-primary btn-sm"
            onClick={saveSocialIntegrations}
            disabled={socialSaving}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            {socialSaving ? 'Salvando...' : 'Salvar configurações'}
          </button>
        </div>
      </div>
    </div>
  );
}
